/**
 * The conversation inbox (tracker item 56): customer emails and portal
 * messages as conversations. Conversations tab: filters (status, assignee,
 * unread, search; a customer or job filter from their pages), the list and
 * the open thread. Unmatched tab: inbound mail with no customer (attach,
 * merge, discard). "New conversation" emails a customer.
 *
 * Routes: /inbox, /inbox/:conversationId, /inbox/unmatched[/:conversationId].
 * Who may open it follows the company's inboxAccess setting; the server
 * answers 403 INBOX_ACCESS_DENIED otherwise and the page says so.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi, IConversationsApi } from '../../api';
import * as types from '../../api/types';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { showToast } from '../../components/Toast';
import { getErrorCode } from '../../utils/apiError';
import { ConversationThread } from './ConversationThread';
import { NewConversationForm } from './NewConversationForm';
import {
  CONVERSATION_STATUS_LABELS,
  INBOX_PATH,
  INBOX_UNMATCHED_PATH,
  conversationErrorMessage,
  conversationStatusClass,
  formatWhen,
  inboxThreadPath,
} from './inboxUi';

const PAGE_SIZE = 25;

export function InboxPage({ tab = 'conversations' }: { tab?: 'conversations' | 'unmatched' }) {
  const api = useMemo<IConversationsApi & ICompanySettingsApi>(() => createAPIClient(), []);
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get('customerId') ?? '';
  const jobFilter = searchParams.get('jobId') ?? '';

  const [status, setStatus] = useState<types.StaffConversationStatus | ''>('');
  const [assignee, setAssignee] = useState<'' | 'me' | 'none'>('');
  const [unread, setUnread] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<types.ConversationPage<types.Conversation | types.UnmatchedConversation> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [inboxAccess, setInboxAccess] = useState<types.InboxAccess>('STAFF');
  const requestId = useRef(0);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const settings = await api.getCompanySettings();
        if (active) setInboxAccess(settings.inboxAccess ?? 'STAFF');
      } catch {
        // The default only narrows the assignee list
      }
    })();
    return () => { active = false; };
  }, [api]);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const data = tab === 'unmatched'
        ? await api.listUnmatchedConversations({ page, limit: PAGE_SIZE })
        : await api.listConversations({
            status, assignee: assignee || undefined, unread: unread || undefined, search: query || undefined,
            customerId: customerFilter || undefined, jobId: jobFilter || undefined, page, limit: PAGE_SIZE,
          });
      if (id === requestId.current) setResult(data);
    } catch (cause) {
      if (id !== requestId.current) return;
      if (getErrorCode(cause) === 'INBOX_ACCESS_DENIED') setDenied(true);
      else setError(conversationErrorMessage(cause, 'Could not load the inbox'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [api, tab, status, assignee, unread, query, customerFilter, jobFilter, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab]);

  const base = tab === 'unmatched' ? INBOX_UNMATCHED_PATH : INBOX_PATH;
  const open = (id: string) => navigate(`${base}/${encodeURIComponent(id)}${tab === 'conversations' && searchParams.toString() ? `?${searchParams}` : ''}`);
  const rows = result?.data ?? [];
  const pages = result?.pagination.pages ?? 1;

  if (denied) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
        <p role="alert" className="mt-4 rounded bg-amber-50 p-4 text-sm text-amber-900">
          The inbox is not available to your role. An administrator can open it to everyone in Settings &gt; Company.
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
        <button type="button" className={primaryButtonClass} onClick={() => setCreating(true)}>New conversation</button>
      </div>

      <nav aria-label="Inbox tabs" className="mt-4 flex gap-2 border-b border-gray-200">
        {[{ key: 'conversations', label: 'Conversations', to: INBOX_PATH }, { key: 'unmatched', label: 'Unmatched', to: INBOX_UNMATCHED_PATH }].map((t) => (
          <Link
            key={t.key}
            to={t.to}
            aria-current={tab === t.key ? 'page' : undefined}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${tab === t.key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      {creating && (
        <section aria-label="Start a conversation" className="mt-4 rounded-lg bg-white p-4 shadow">
          <h2 className="mb-3 text-lg font-semibold text-gray-900">New conversation</h2>
          <NewConversationForm
            onCancel={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false);
              showToast(created.message.delivery?.status === 'SKIPPED' ? 'warning' : 'success',
                created.message.delivery?.status === 'SKIPPED' ? `Conversation saved, but the email was not sent: ${created.message.delivery.skipReasonText ?? created.message.delivery.skipReason}` : 'Email queued');
              void load();
              navigate(inboxThreadPath(created.conversation.id));
            }}
          />
        </section>
      )}

      {tab === 'conversations' && (
        <form
          className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[11rem_11rem_auto_1fr_auto] sm:items-end"
          onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(search.trim()); }}
        >
          <div>
            <label htmlFor="inbox-status" className={labelClass}>Status</label>
            <select id="inbox-status" className={inputClass} value={status} onChange={(e) => { setPage(1); setStatus(e.target.value as types.StaffConversationStatus | ''); }}>
              <option value="">All</option>
              {types.STAFF_CONVERSATION_STATUSES.map((s) => <option key={s} value={s}>{CONVERSATION_STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="inbox-assignee" className={labelClass}>Assigned</label>
            <select id="inbox-assignee" className={inputClass} value={assignee} onChange={(e) => { setPage(1); setAssignee(e.target.value as '' | 'me' | 'none'); }}>
              <option value="">Anyone</option>
              <option value="me">Assigned to me</option>
              <option value="none">Unassigned</option>
            </select>
          </div>
          <label className="flex min-h-[42px] items-center gap-2 text-sm text-gray-700">
            <input id="inbox-unread" type="checkbox" checked={unread} onChange={(e) => { setPage(1); setUnread(e.target.checked); }} />
            Unread only
          </label>
          <div>
            <label htmlFor="inbox-search" className={labelClass}>Search</label>
            <input id="inbox-search" className={inputClass} value={search} placeholder="Subject, customer name or email" onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button type="submit" className={secondaryButtonClass}>Search</button>
        </form>
      )}

      {(customerFilter || jobFilter) && tab === 'conversations' && (
        <p className="mt-3 text-sm text-gray-700" data-testid="inbox-filter">
          Showing conversations {customerFilter ? 'of one customer' : 'about one job'}.{' '}
          <button type="button" className="font-semibold text-blue-700 hover:underline" onClick={() => setSearchParams({})}>Show all</button>
        </p>
      )}

      {error && <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <div>
          {loading && !result && <p role="status" className="text-gray-600">Loading…</p>}
          {result && rows.length === 0 && !loading && (
            <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 shadow">
              {tab === 'unmatched' ? 'No unmatched email.' : status || assignee || unread || query || customerFilter || jobFilter ? 'No conversations match these filters.' : 'No conversations yet.'}
            </p>
          )}
          {rows.length > 0 && (
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white shadow" aria-label={tab === 'unmatched' ? 'Unmatched email' : 'Conversations'}>
              {rows.map((c) => {
                const unmatched = 'from' in c ? (c as types.UnmatchedConversation) : null;
                const selected = c.id === conversationId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => open(c.id)}
                      aria-current={selected ? 'true' : undefined}
                      className={`block w-full px-4 py-3 text-left hover:bg-gray-50 ${selected ? 'bg-blue-50' : ''}`}
                      data-testid="conversation-row"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={`truncate text-sm ${c.unread ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
                          {unmatched ? unmatched.from ?? 'Unknown sender' : c.customer?.name ?? 'No customer'}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">{formatWhen(c.lastMessageAt)}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2">
                        {c.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" aria-label="Unread" />}
                        <span className="truncate text-sm text-gray-700">{c.subject}</span>
                      </div>
                      {unmatched ? (
                        <p className="mt-0.5 truncate text-xs text-gray-500">{unmatched.preview}</p>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-1 text-xs">
                          <span className={`rounded px-1.5 py-0.5 font-semibold ${conversationStatusClass(c.status)}`}>{CONVERSATION_STATUS_LABELS[c.status]}</span>
                          {c.origin === 'PORTAL' && <span className="rounded bg-teal-100 px-1.5 py-0.5 text-teal-800">Portal</span>}
                          {c.assignee && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{c.assignee.name}</span>}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-between gap-2 text-sm">
              <button type="button" className={secondaryButtonClass} disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span>Page {page} of {pages}</span>
              <button type="button" className={secondaryButtonClass} disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          )}
        </div>

        <div className="min-w-0 rounded-lg bg-white p-4 shadow">
          {conversationId ? (
            <ConversationThread
              key={conversationId}
              conversationId={conversationId}
              inboxAccess={inboxAccess}
              onChanged={() => void load()}
              onRemoved={(mergedInto) => { void load(); navigate(mergedInto ? inboxThreadPath(mergedInto) : base); }}
            />
          ) : (
            <p className="p-6 text-center text-sm text-gray-600">Choose a conversation to read it.</p>
          )}
        </div>
      </div>
    </main>
  );
}
